import {
  GUEST_REFETCH_FIELDS,
  getGuestsByIds,
} from "@/lib/api/handlers/GuestHandling";
import { Guest } from "@/types/Guest";
import { useTranslations } from "next-intl";
import { useState, useCallback } from "react";

export const useCompanions = ({ eventId }: { eventId: string }) => {
  const t = useTranslations("errors");

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [guestDetails, setGuestDetails] = useState<{
    open: boolean;
    guest?: Guest;
  }>({
    open: false,
  });

  const processCompanionClick = useCallback(
    // Handle dropdown action selection
    async (guestId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setLoading(true);

      try {
        const result = await getGuestsByIds(
          eventId as string,
          [guestId],
          "",
          [],
          // Shared projection: this opens the SAME detail popup, so it needs
          // the same fields — notably `eventId`, without which that popup's
          // refetch bailed out, and origin/importId for its display-only cells.
          `${GUEST_REFETCH_FIELDS},companions`
        );
        if (Array.isArray(result)) {
          const relevantCompanion = result[0];
          setGuestDetails({
            open: true,
            guest: relevantCompanion,
          });
        } else if (
          result &&
          typeof result === "object" &&
          "isServerError" in result
        ) {
          throw Error(result.message);
        } else {
          throw Error("unknown");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        setErrorMessage(t(errorMessage));
      } finally {
        setLoading(false);
      }
    },
    [eventId, setLoading, setGuestDetails, setErrorMessage]
  );

  return {
    processCompanionClick,
    errorMessage,
    loading,
    guestDetails,
    setErrorMessage,
    setGuestDetails,
  };
};
